<script setup lang="ts">
import speakerIcon from '../../assets/icons/speaker.svg'
import buttonReflectionIcon from '../../assets/icons/button-reflection.svg'
import { onBeforeUnmount, ref } from 'vue';
import { audioManager } from '../../utils/audioManager';
import { QuizAudioKey } from '../../types/quiz';
import { utils } from '../../utils';
import { PauseIcon, PlayIcon } from '@heroicons/vue/16/solid';

// TODO: Refactor logic and event listener
const { src, label = '', audioKey = 'session', controls = false, bubles = true } = defineProps<{ src: string, label?: string, audioKey?: string, controls?: boolean, bubles?: boolean }>()

const isPlaying = ref<boolean>(false);
const currentTime = ref<number>(0);
const duration = ref<number>(0);
const audioPlayer = ref<HTMLAudioElement | null>(null);
const originalVolume = ref<number>(1);

const togglePlay = (): void => {
    if (isPlaying.value) {
        audioManager.pause(audioKey)
        volumeShift()
    } else {
        audioPlayer.value = audioManager.play(src, audioKey, { loop: false, volume: 1, muted: false });
        volumeShift()
        audioPlayer.value.addEventListener('loadedmetadata', () => {
            duration.value = audioPlayer.value?.duration || 0;
        });
        audioPlayer.value.addEventListener('timeupdate', () => {
            currentTime.value = audioPlayer.value?.currentTime || 0;
        });
        audioPlayer.value.addEventListener('ended', () => {
            volumeShift()
            isPlaying.value = false;
        });
        audioPlayer.value.addEventListener('pause', () => {
            volumeShift()
            isPlaying.value = false;
        });
    }
    isPlaying.value = !isPlaying.value;
};

function volumeShift(): void {
    //get all the keys of the audios that are playing and filter out the BGM key
    const otherAudios = audioManager.getPlayingAudiosKeys().filter((key) => key !== QuizAudioKey.BGM);
    //set the volume of BGM to 10% if there are other audios playing
    if (otherAudios.length > 0) {
        if (originalVolume.value === 1) {
            originalVolume.value = audioManager.getVolume(QuizAudioKey.BGM).volume || 1;
        }
        //set the volume of BGM to 10%
        audioManager.setVolume(QuizAudioKey.BGM, 0.1)
    } else {
        //set the volume of BGM to 100%
        audioManager.setVolume(QuizAudioKey.BGM, originalVolume.value)
    }
}

onBeforeUnmount(() => {
    audioManager.stop(audioKey)
    audioManager.setVolume(QuizAudioKey.BGM, 1)

    audioPlayer.value?.removeEventListener('loadedmetadata', () => {
        duration.value = audioPlayer.value?.duration || 0;
    });
    audioPlayer.value?.removeEventListener('timeupdate', () => {
        currentTime.value = audioPlayer.value?.currentTime || 0;
    });

    audioPlayer.value?.removeEventListener('ended', () => {
        isPlaying.value = false;
    });
    audioPlayer.value?.removeEventListener('pause', () => {
        isPlaying.value = false;
    });

    isPlaying.value = false;
})
</script>

<template>
    <!-- Custom Controls -->
    <div v-if="controls" class="flex items-center gap-3 w-full bg-white border border-orange-600 dark:border-fuchsia-500 p-3 rounded-full">
        <!-- Hidden native audio element -->
        <button @click="togglePlay" class="p-2 bg-none rounded-full hover:bg-purple-100">
            <PlayIcon v-if="!isPlaying" class="size-5 fill-orange-600 dark:fill-fuchsia-500" />
            <PauseIcon v-else class="size-5 fill-orange-600 dark:fill-fuchsia-500" />
        </button>
        <!-- Progress Bar -->
        <input type="range" min="0" :max="duration" step="0.1" v-model="currentTime" class="transistion-all duration-100 w-full h-1 accent-orange-600 dark:accent-fuchsia-500 disabled:accent-fuchsia-500" />
        <span class="text-sm text-orange-600 dark:text-fuchsia-500">
            {{ utils.formatSecondsToMinuteSeconds(Math.floor(currentTime % 60), 'mm:ss') }}
        </span>
    </div>
    <div v-else
        class="audio-player flex items-center justify-center gap-2 shrink-0 bg-orange-500 hover:bg-orange-600 focus:bg-orange-600 transition-all cursor-pointer py-2 px-4 rounded-3xl relative shadow-[0px_-2px_0_0_#00000033_inset,0px_2px_0_0_#FFFFFF4D_inset]"
        :class="{ 'animate-pulse': isPlaying }" @click="togglePlay">
        <img v-if="bubles" :src="buttonReflectionIcon" class="absolute top-1 left-2 pointer-events-none" />
        <span v-if="label" class="text-white whitespace-pre">{{ label }}</span>
        <img :src="speakerIcon" class="size-5 pointer-events-none" />
    </div>
</template>